<template>
  <div>
    <div class="section-head">
      <div class="d-flex justify-content-between align-items-center">
        <h2>
          {{ $t('objectDescription:description') }}
        </h2>
        <language-switch
          class="descriptionLanguageSwitch"
          :language-codes="nativeIsoCodes"
          :translator-codes="translatorIsoCodes"
          icon="chevrondown"
          :zindex-class="999"
          @lang-changed="switchDescriptionLanguage"
        />
      </div>
    </div>
    <div class="section-main">
      <div
        v-if="translator"
        :key="'translator'"
        class="pb-2"
      >
        {{ $t('object:translatorhint.'+targetLanguage,{translator:$t('object:translationservice.'+translator)}) }}
      </div>

      <div
        :key="'text'"
      >
        <b-collapse id="collapse-object-description" />
        <!-- Objektbeschreibung Start -->

        <div
          class="descriptionText-full"
          :class="{fade:descriptionTextHidden.length > 0}"
        >
          <div
            v-if="descriptionTextVisible !== 'na'"
            v-dompurify-html="descriptionText"
          />
          <!-- Objektbeschreibung Ende -->
          <div v-if="possibleSource && descriptionTextVisible === 'na'">
            <translator-hint
              :src-language="possibleSource"
              @lang-changed="switchDescriptionLanguage"
            />
          </div>

          <agency-logo
            v-if="showAgencyLogo"
            :agency-number="getObjectDataByKey('agency') || 0"
            :template="'agencyLogoObject'"
          />
        </div>
      </div>

      <div
        v-show="descriptionTextHidden.length > 0"
        class="section-more"
      >
        <a
          v-b-toggle.collapse-object-description
          class="btn btn-link btn-collapse show-object-description"
        >
          <span class="more">
            <i class="icon">
              <icon-chevron-down />
            </i>
            {{ $t('objectDescription:completeDescription') }}
          </span>
        </a>
        <a
          v-smooth-scroll="{ duration: 200, updateHistory: false }"
          v-b-toggle.collapse-object-description
          href="#beschreibung"
          class="btn btn-link btn-collapse hide-object-description"
        >
          <span class="less">
            <i class="icon icon-only">
              <icon-chevron-up />
            </i>
            {{ $t('objectDescription:showLess') }}
          </span>
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import LanguageSwitch from 'components/LanguageSwitch.vue';
import { difference } from 'utils/Object';
import { stringRegexLastIndexOf } from 'utils/StringHelper';
import ActiveObjectStore from 'mixins/ActiveObjectStore';
import DeviceHelper from 'mixins/DeviceHelper';
import AgencyLogo from 'components/AgencyLogo.vue';
import TranslatorHint from 'components/TranslatorHint.vue';

import { BCollapse, VBToggle } from 'bootstrap-vue'; //

/* ICONS */
import IconChevronUp from 'ICON:IconChevronUp';
import IconChevronDown from 'ICON:IconChevronDown';
/** STORE */
import { mapMutations } from 'vuex';

export default {
  name: 'ObjectDescription',
  i18nOptions: { namespaces: 'objectDescription' },
  components: {
    BCollapse,
    LanguageSwitch,
    AgencyLogo,
    IconChevronUp,
    IconChevronDown,
    TranslatorHint,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  mixins: [ActiveObjectStore, DeviceHelper],
  props: {
    showAgencyLogo: {
      type: Boolean,
      default: false,
    },
    // EDVUE-48 Es werden stets alle Sprachen angeboten.
    // nativeLanguages: {
    //   type: Array,
    //   default: () => ([]),
    // },
  },
  data() {
    return {
      descText: '',
      // all codes, shown in LanguageSwitch
      possibleLanguages: ['de', 'en', 'fr', 'it', 'es', 'pl'],
      native: true,
      translator: '',
      targetLanguage: '',
      currentLangAmenities: {},
    };
  },
  computed: {
    descriptions() {
      return this.getObjectDataByKey('descriptions');
    },
    descriptionText() {
      const descStr = this.descText || this.descriptions.natives[this.$store.state.app.currentLanguage]?.text || '';
      return descStr;// .replace(/<(\/)*ul>/gi, '<$1div>').replace(/<li>/gi, '<span class="item">').replace(/<[/]{1}li>/gi, '</span>');
    },
    descriptionLanguages() { // native available languages
      return this.getObjectDataByKey('descriptionLanguages');
    },
    descriptionVisibleLength() {
      // show 1000 chars on lg and xl; show 500 chars on xs, sm and md
      let descriptionVisibleLength = 500;
      if (['lg', 'xl'].includes(this.dh_breakpoint)) {
        descriptionVisibleLength = 1000;
      }
      return descriptionVisibleLength;
    },
    descriptionTextVisible() {
      if (!this.hasObjectData) return '';

      let text = this.descText || this.descriptions.natives[this.$store.state.app.currentLanguage].text || '';

      if (text !== 'na') {
      // don't split text when length smaller than descriptionVisibleLength or text contains <table>
        if (text.length > this.descriptionVisibleLength) {
          text = text.substr(0, this.descriptionVisibleLength);

          // visible text should end with complete sentence or <br> if it comes later
          const textCutNewLine = stringRegexLastIndexOf(text, /\.\n|\. /i);
          const textCutBrTag = stringRegexLastIndexOf(text, /<br[^>]*>/i);
          // check which text ending comes later and use it for cutting
          let textCut = (textCutNewLine > textCutBrTag ? textCutNewLine : textCutBrTag);
          // sometimes text has no newLine and no br, fallback to default text length
          if (textCut < 10) {
            textCut = this.descriptionVisibleLength;
          }

          // finally create the visibile text
          text = text.substr(
            0, Math.min(text.length, textCut),
          );
        }
      }
      return text;
    },
    descriptionTextHidden() {
      if (!this.hasObjectData) return '';
      // remaining description text, only visible after user clicks toggle button
      const visibleLength = this.descriptionTextVisible.length;
      let text = this.descText || this.descriptions.natives[this.$store.state.app.currentLanguage]?.text || '';
      text = text.substr(visibleLength);
      return text;
    },
    nativeIsoCodes() {
      // console.log('this.possibleLanguages, this.descriptionLanguages', this.possibleLanguages, this.descriptionLanguages);
      return this.possibleLanguages;
    },
    translatorIsoCodes() {
      return difference(this.possibleLanguages, this.descriptionLanguages);
    },
    possibleSource() {
      if (this.descriptionLanguages.length) { return this.descriptionLanguages[0]; }
      return false;
    },
  },
  beforeDestroy() {
    this.$i18n.i18next.reloadResources(this.$i18n.i18next.language, 'objectDescription');
  },
  methods: {
    ...mapMutations({ setObjectDescriptionLanguage: 'SET_OBJECT_DESCRIPTION_LANGUAGE' }),
    switchDescriptionLanguage(languageCode) {
      let translationSource = null;
      let sourceLanguageCode = null;
      if (this.descriptionLanguages.length) {
        [sourceLanguageCode] = this.descriptionLanguages;
        if (this.descriptionLanguages.indexOf(languageCode) < 0) { // text not available, translation needed
          translationSource = sourceLanguageCode;
          this.native = false;
        } else {
          this.native = true;
        }
        // load description from API
        this.getObjectDescription(this.activeObjectNumber, languageCode, translationSource);
      }

      /**
       * overwrite language for amenities only, stay other language json
       */
      // load target language amenities
      this.$i18n.i18next.reloadResources(languageCode, 'objectDescription').then(() => {
      // get data for target language
        const foreignAmenities = this.$i18n.i18next.getDataByLanguage(languageCode);
        // remember current language amenities
        this.currentLangAmenities = {
          ...this.$i18n.i18next.getDataByLanguage(
            this.$i18n.i18next.language,
          ),
        };
        this.$i18n.i18next.addResourceBundle(
          // overwrite current amenities with target language amenities
          this.$i18n.i18next.language, 'objectDescription', foreignAmenities.objectDescription, true, true,
        );
      });
      this.setObjectDescriptionLanguage(languageCode);
    },
    getObjectDescription(activeObjectNumber, languageCode, translationSource) {
      return this.$store
        .dispatch('GET_OBJECT_DESCRIPTION', {
          objectNumber: activeObjectNumber,
          languageCode,
          translationSource,
        })
        .then(() => {
          this.getDescriptons(languageCode);
        })
        .catch(() => {});
    },
    getDescriptons(languageCode) {
      const objectDecriptions = this.getObjectDataByKey('descriptions');
      const textSrc = this.native ? 'natives' : 'translation';
      const lang = languageCode;
      this.translator = this.native ? false : objectDecriptions[textSrc][lang].translator;
      this.targetLanguage = this.native ? '' : objectDecriptions[textSrc][lang].languageCode;
      this.descText = objectDecriptions[textSrc][lang].text;
    },
  },
};
</script>
<style lang="scss" scoped>
  .collapse{
    &.show{
      & + .descriptionText-full.fade{
        display: block;
        height:auto;
        &:after{ /* reset fading container */
          background: #FFF;
          height:0;
        }
      }
    }
  }
  .fade {
    position: relative;
    height: 12em;
    overflow:hidden;
  }
  .fade:after {
    content: "";
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 11em;/* roughly */
    background: linear-gradient(180deg, hsla(255,255,255,0), hsla(255,255,255,1) 100%);
  }
</style>
